import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, PhoneCall, Truck, Sparkles, ChevronRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop All', path: '/shop' },
    { name: 'About', path: '/about' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Slim promo strip */}
      <div className="bg-gradient-to-r from-athletic-500 via-power-500 to-athletic-600 text-white text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-center gap-2 text-center">
          <Sparkles className="w-3.5 h-3.5 hidden sm:inline-block" />
          <p className="font-semibold tracking-wide">
            <span className="font-extrabold">20% OFF</span> your first order &mdash; code{' '}
            <span className="inline-block px-2 py-0.5 mx-0.5 bg-white text-power-600 font-extrabold tracking-wider rounded">BRIGHT20</span>
          </p>
        </div>
      </div>

      {/* Utility bar (navy) */}
      <div className="bg-dark-900 text-dark-200 text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
          <p className="hidden sm:flex items-center gap-2 tracking-wide">
            <Truck className="w-3.5 h-3.5 text-athletic-400" />
            <span className="font-semibold text-white">Free USPS Shipping</span> on every US order
          </p>
          <div className="flex items-center gap-4 tracking-wide">
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden md:inline hover:text-white transition-colors">{CONTACT_INFO.email}</a>
            <a href={phoneHref} className="flex items-center gap-1.5 font-semibold text-white hover:text-athletic-400 transition-colors">
              <PhoneCall className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="bg-white/95 backdrop-blur border-b border-dark-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[70px] gap-4">
            <Logo />

            {/* Desktop primary nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      isActive
                        ? 'text-athletic-600 bg-athletic-50'
                        : 'text-dark-700 hover:text-athletic-600 hover:bg-dark-50'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-2 sm:gap-3">
              <a href={phoneHref} className="hidden xl:flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-900 text-white text-sm font-semibold hover:bg-dark-800 transition-colors">
                <PhoneCall className="w-4 h-4 text-athletic-400" /> Call an Expert
              </a>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-xl flex items-center justify-center text-dark-800 hover:text-athletic-600 bg-dark-50 hover:bg-athletic-50 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gradient-to-br from-athletic-500 to-power-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-xl flex items-center justify-center text-dark-800 bg-dark-50 hover:bg-dark-100"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Category strip (desktop) */}
        <div className="hidden lg:block border-t border-dark-100 bg-dark-50/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-1 h-11 overflow-x-auto">
              <Link to="/shop" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] font-bold text-athletic-700 hover:bg-athletic-50 whitespace-nowrap">
                <Sparkles className="w-3.5 h-3.5" /> Shop All
              </Link>
              <span className="text-dark-200">/</span>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="px-3 py-1.5 rounded-md text-[13px] font-medium text-dark-600 hover:text-athletic-600 hover:bg-white whitespace-nowrap transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-100 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-700 hover:bg-athletic-50 hover:text-athletic-600 rounded-lg"
                >
                  {link.name}
                  <ChevronRight className="w-4 h-4 text-dark-300" />
                </Link>
              ))}
              <div className="pt-3 border-t border-dark-100 mt-3">
                <p className="px-4 text-xs uppercase tracking-wider text-dark-400 mb-2">Shop by Category</p>
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/shop?category=${cat.slug}`}
                    className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-athletic-50 hover:text-athletic-600 rounded-lg"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-lg bg-dark-900 text-white font-semibold">
                <PhoneCall className="w-4 h-4 text-athletic-400" /> Call {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
